import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fabricsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, Plus, Trash2, Eye, User, X, ZoomIn, ZoomOut, Link, Camera } from 'lucide-react';
import { TryOnFabricPanel } from '@/components/TryOnFabricPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveSwatchUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

const PATTERN_TYPES = ['solid', 'stripe', 'check', 'herringbone', 'plaid', 'print', 'texture', 'houndstooth'];
const OCCASION_TAGS = ['work', 'wedding', 'casual', 'formal', 'ethnic', 'daily'];
const SEASONS = ['summer', 'winter', 'all_season', 'transitional'];
const PRICE_TIERS = ['standard', 'premium', 'luxury'];
const BODY_TYPES = ['all', 'athletic', 'slim', 'stocky', 'tall_lean', 'heavy_set'];
const COMPLEXIONS = ['spring', 'summer', 'autumn', 'winter', 'all'];

const EMPTY_FORM = {
  internal_code: '', vendor_code: '', mill_name: '', composition: '',
  weight_gsm: '', width_cm: '150', colour_primary: '', colour_secondary: '',
  pattern_type: 'solid', occasion_tags: [], season: 'all_season',
  price_per_metre: '', price_tier: 'standard', vendor_name: '',
  lead_time_days: '', is_available: true, suitable_body_types: ['all'],
  suitable_complexions: ['all'], notes: '',
};

export default function FabricFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === 'new';
  const isAdmin = user?.role === 'Admin';
  const isReadOnly = !isAdmin;

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState({ alias_text: '', vendor_name: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fabricId, setFabricId] = useState(id);
  const [swatchLightboxOpen, setSwatchLightboxOpen] = useState(false);
  const [swatchZoom, setSwatchZoom] = useState(1);
  const [tryOnPanelOpen, setTryOnPanelOpen] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [settingUrl, setSettingUrl] = useState(false);

  const canTryOn = user?.role === 'Admin' || user?.role === 'Sales';

  useEffect(() => {
    if (!isNew && id) {
      fetchFabric();
      fetchAliases();
    }
  }, [id, isNew]);

  const fetchFabric = async () => {
    try {
      const response = await fabricsAPI.get(id);
      const fabric = response.data;
      setForm({
        internal_code: fabric.internal_code || '',
        vendor_code: fabric.vendor_code || '',
        mill_name: fabric.mill_name || '',
        composition: fabric.composition || '',
        weight_gsm: fabric.weight_gsm != null ? String(fabric.weight_gsm) : '',
        width_cm: String(fabric.width_cm || 150),
        colour_primary: fabric.colour_primary || '',
        colour_secondary: fabric.colour_secondary || '',
        pattern_type: fabric.pattern_type || 'solid',
        occasion_tags: fabric.occasion_tags || [],
        season: fabric.season || 'all_season',
        price_per_metre: fabric.price_per_metre != null ? String(fabric.price_per_metre) : '',
        price_tier: fabric.price_tier || 'standard',
        vendor_name: fabric.vendor_name || '',
        lead_time_days: fabric.lead_time_days != null ? String(fabric.lead_time_days) : '',
        is_available: fabric.is_available !== false,
        suitable_body_types: fabric.suitable_body_types || ['all'],
        suitable_complexions: fabric.suitable_complexions || ['all'],
        notes: fabric.notes || '',
        swatch_image_url: fabric.swatch_image_url || '',
      });
      setFabricId(fabric.id);
    } catch (err) {
      toast.error('Failed to load fabric');
      navigate('/app/fabrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAliases = async () => {
    try {
      const response = await fabricsAPI.listAliases(id);
      setAliases(response.data);
    } catch (err) {
      console.error('Failed to load aliases');
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.internal_code.trim()) errs.internal_code = 'Required';
    if (!form.vendor_code.trim()) errs.vendor_code = 'Required';
    if (!form.mill_name.trim()) errs.mill_name = 'Required';
    if (!form.composition.trim()) errs.composition = 'Required';
    if (!form.width_cm || isNaN(form.width_cm)) errs.width_cm = 'Required number';
    if (!form.colour_primary.trim()) errs.colour_primary = 'Required';
    if (!form.price_per_metre || isNaN(form.price_per_metre)) errs.price_per_metre = 'Required number';
    if (!form.vendor_name.trim()) errs.vendor_name = 'Required';
    if (!form.lead_time_days || isNaN(form.lead_time_days)) errs.lead_time_days = 'Required number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        weight_gsm: form.weight_gsm ? parseInt(form.weight_gsm) : null,
        width_cm: parseInt(form.width_cm),
        price_per_metre: parseFloat(form.price_per_metre),
        lead_time_days: parseInt(form.lead_time_days),
        colour_secondary: form.colour_secondary || null,
        notes: form.notes || null,
      };
      delete payload.swatch_image_url;

      if (isNew) {
        const response = await fabricsAPI.create(payload);
        toast.success('Fabric created successfully');
        navigate(`/app/fabrics/${response.data.id}`);
      } else {
        await fabricsAPI.update(id, payload);
        toast.success('Fabric updated successfully');
        fetchFabric();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save fabric';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!fabricId || isNew) {
      toast.error('Please save the fabric first, then upload an image.');
      return;
    }
    setUploadingImage(true);
    try {
      const response = await fabricsAPI.uploadSwatch(fabricId, file);
      setForm(prev => ({ ...prev, swatch_image_url: response.data.swatch_image_url }));
      toast.success('Swatch image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSetImageUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    setSettingUrl(true);
    try {
      const response = await fabricsAPI.setSwatchUrl(fabricId, url);
      setForm(prev => ({ ...prev, swatch_image_url: response.data.swatch_image_url }));
      setImageUrlInput('');
      setShowUrlInput(false);
      toast.success('Swatch image URL set');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set image URL');
    } finally {
      setSettingUrl(false);
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!fabricId || isNew) {
      toast.error('Please save the fabric first.');
      return;
    }
    setUploadingImage(true);
    try {
      const response = await fabricsAPI.uploadSwatch(fabricId, file);
      setForm(prev => ({ ...prev, swatch_image_url: response.data.swatch_image_url }));
      toast.success('Photo captured and uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddAlias = async () => {
    if (!newAlias.alias_text.trim() || !newAlias.vendor_name.trim()) {
      toast.error('Both alias text and vendor name are required');
      return;
    }
    try {
      await fabricsAPI.createAlias(fabricId, newAlias);
      toast.success('Alias added');
      setNewAlias({ alias_text: '', vendor_name: '' });
      fetchAliases();
    } catch (err) {
      toast.error('Failed to add alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      await fabricsAPI.deleteAlias(aliasId);
      toast.success('Alias deleted');
      fetchAliases();
    } catch (err) {
      toast.error('Failed to delete alias');
    }
  };

  const toggleOccasion = (tag) => {
    setForm(prev => ({
      ...prev,
      occasion_tags: prev.occasion_tags.includes(tag)
        ? prev.occasion_tags.filter(t => t !== tag)
        : [...prev.occasion_tags, tag]
    }));
  };

  const toggleBodyType = (bt) => {
    setForm(prev => ({
      ...prev,
      suitable_body_types: prev.suitable_body_types.includes(bt)
        ? prev.suitable_body_types.filter(t => t !== bt)
        : [...prev.suitable_body_types, bt]
    }));
  };

  const toggleComplexion = (c) => {
    setForm(prev => ({
      ...prev,
      suitable_complexions: prev.suitable_complexions.includes(c)
        ? prev.suitable_complexions.filter(t => t !== c)
        : [...prev.suitable_complexions, c]
    }));
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Card className="rounded-xl"><CardContent className="p-6 space-y-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/fabrics')} className="h-9 px-2">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }}>
            {isNew ? 'Add New Fabric' : (isReadOnly ? 'Fabric Details' : 'Edit Fabric')}
          </h1>
          {!isNew && <p className="text-sm mt-0.5" style={{ color: 'var(--ink-muted)' }}>{form.internal_code}</p>}
        </div>
        {!isReadOnly && (
          <Button
            data-testid="fabric-save-button"
            onClick={handleSave}
            disabled={saving}
            className="h-11 rounded-xl px-6 text-sm font-medium"
            style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Basic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Internal Code *</Label>
                  <Input value={form.internal_code} onChange={(e) => updateField('internal_code', e.target.value)} placeholder="LP-NVY-S150-150" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.internal_code && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.internal_code}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Vendor Code *</Label>
                  <Input value={form.vendor_code} onChange={(e) => updateField('vendor_code', e.target.value)} placeholder="Loro Piana 1234-A" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.vendor_code && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.vendor_code}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Mill Name *</Label>
                  <Input value={form.mill_name} onChange={(e) => updateField('mill_name', e.target.value)} placeholder="Loro Piana" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.mill_name && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.mill_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Composition *</Label>
                  <Input value={form.composition} onChange={(e) => updateField('composition', e.target.value)} placeholder="100% Merino Wool" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.composition && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.composition}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Weight (GSM)</Label>
                  <Input type="number" value={form.weight_gsm} onChange={(e) => updateField('weight_gsm', e.target.value)} placeholder="260" className="h-11 rounded-lg" disabled={isReadOnly} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Width (cm) *</Label>
                  <Input type="number" value={form.width_cm} onChange={(e) => updateField('width_cm', e.target.value)} placeholder="150" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.width_cm && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.width_cm}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Colour & Pattern */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Colour & Pattern</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Primary Colour *</Label>
                  <Input value={form.colour_primary} onChange={(e) => updateField('colour_primary', e.target.value)} placeholder="Navy" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.colour_primary && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.colour_primary}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Secondary Colour</Label>
                  <Input value={form.colour_secondary} onChange={(e) => updateField('colour_secondary', e.target.value)} placeholder="Optional" className="h-11 rounded-lg" disabled={isReadOnly} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Pattern Type *</Label>
                  <Select value={form.pattern_type} onValueChange={(v) => updateField('pattern_type', v)} disabled={isReadOnly}>
                    <SelectTrigger className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PATTERN_TYPES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Season *</Label>
                  <Select value={form.season} onValueChange={(v) => updateField('season', v)} disabled={isReadOnly}>
                    <SelectTrigger className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEASONS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Occasion Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {OCCASION_TAGS.map(tag => (
                    <button key={tag} type="button" onClick={() => !isReadOnly && toggleOccasion(tag)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border capitalize ${form.occasion_tags.includes(tag) ? 'bg-[rgba(27,42,74,0.08)] border-[rgba(27,42,74,0.2)] text-[#1B2A4A]' : 'bg-white border-border/60 text-[#475467]'}`}
                      style={{ transition: 'background-color 150ms, border-color 150ms' }} disabled={isReadOnly}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Vendor */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Pricing & Vendor</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Price per Metre (INR) *</Label>
                  <Input type="number" value={form.price_per_metre} onChange={(e) => updateField('price_per_metre', e.target.value)} placeholder="5000" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.price_per_metre && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.price_per_metre}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Price Tier *</Label>
                  <Select value={form.price_tier} onValueChange={(v) => updateField('price_tier', v)} disabled={isReadOnly}>
                    <SelectTrigger className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRICE_TIERS.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Vendor Name *</Label>
                  <Input value={form.vendor_name} onChange={(e) => updateField('vendor_name', e.target.value)} placeholder="Italian Textiles Mumbai" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.vendor_name && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.vendor_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Lead Time (days) *</Label>
                  <Input type="number" value={form.lead_time_days} onChange={(e) => updateField('lead_time_days', e.target.value)} placeholder="21" className="h-11 rounded-lg" disabled={isReadOnly} />
                  {errors.lead_time_days && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.lead_time_days}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Body Types & Complexions */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Suitability</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Suitable Body Types</Label>
                <div className="flex flex-wrap gap-2">
                  {BODY_TYPES.map(bt => (
                    <button key={bt} type="button" onClick={() => !isReadOnly && toggleBodyType(bt)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border capitalize ${form.suitable_body_types.includes(bt) ? 'bg-[rgba(27,42,74,0.08)] border-[rgba(27,42,74,0.2)] text-[#1B2A4A]' : 'bg-white border-border/60 text-[#475467]'}`}
                      style={{ transition: 'background-color 150ms, border-color 150ms' }} disabled={isReadOnly}>
                      {bt.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Suitable Complexions</Label>
                <div className="flex flex-wrap gap-2">
                  {COMPLEXIONS.map(c => (
                    <button key={c} type="button" onClick={() => !isReadOnly && toggleComplexion(c)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border capitalize ${form.suitable_complexions.includes(c) ? 'bg-[rgba(27,42,74,0.08)] border-[rgba(27,42,74,0.2)] text-[#1B2A4A]' : 'bg-white border-border/60 text-[#475467]'}`}
                      style={{ transition: 'background-color 150ms, border-color 150ms' }} disabled={isReadOnly}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Additional notes about this fabric..."
                  className="w-full h-24 px-3 py-2 rounded-lg border border-border/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-2"
                  style={{ backgroundColor: 'white' }}
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Swatch Image */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Swatch Image</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {form.swatch_image_url ? (
                <div 
                  className="rounded-lg overflow-hidden border border-border/60 cursor-pointer group relative"
                  onClick={() => setSwatchLightboxOpen(true)}
                  data-testid="swatch-image-preview"
                >
                  <img
                    src={resolveSwatchUrl(form.swatch_image_url)}
                    alt="Swatch"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 rounded-lg border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-2">
                  <Upload className="w-8 h-8 opacity-20" />
                  <p className="text-sm text-center px-4" style={{ color: 'var(--ink-muted)' }}>No swatch image. Upload or add URL.</p>
                </div>
              )}
              {isAdmin && !isNew && (
                <div className="space-y-2">
                  {/* Upload Image */}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" id="swatch-upload" />
                  <Button data-testid="fabric-upload-button" variant="outline" className="w-full h-10 rounded-xl text-sm" disabled={uploadingImage}
                    onClick={() => document.getElementById('swatch-upload').click()}>
                    <Upload className="w-4 h-4 mr-2" /> {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  
                  {/* Image URL */}
                  {showUrlInput ? (
                    <div className="flex gap-1.5">
                      <Input
                        data-testid="swatch-url-input"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        placeholder="https://example.com/swatch.jpg"
                        className="h-10 rounded-lg text-sm flex-1"
                      />
                      <Button data-testid="swatch-url-save" size="sm" className="h-10 rounded-lg px-3" disabled={settingUrl || !imageUrlInput.trim()}
                        style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}
                        onClick={handleSetImageUrl}>
                        {settingUrl ? '...' : 'Set'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-10 w-10 p-0 rounded-lg"
                        onClick={() => { setShowUrlInput(false); setImageUrlInput(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button data-testid="swatch-url-button" variant="outline" className="w-full h-10 rounded-xl text-sm"
                      onClick={() => setShowUrlInput(true)}>
                      <Link className="w-4 h-4 mr-2" /> Image URL
                    </Button>
                  )}
                  
                  {/* Take Photo (camera) */}
                  <input type="file" accept="image/*" capture="environment" onChange={handleCameraCapture} className="hidden" id="swatch-camera" />
                  <Button data-testid="swatch-camera-button" variant="outline" className="w-full h-10 rounded-xl text-sm" disabled={uploadingImage}
                    onClick={() => document.getElementById('swatch-camera').click()}>
                    <Camera className="w-4 h-4 mr-2" /> Take Photo
                  </Button>
                </div>
              )}
              {isNew && isAdmin && (
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Save the fabric first, then upload a swatch image.</p>
              )}
            </CardContent>
          </Card>

          {/* Try On This Fabric Button */}
          {!isNew && canTryOn && (
            <Card className="rounded-xl border-2 border-[var(--brand-gold)] bg-gradient-to-br from-[rgba(197,165,90,0.08)] to-[rgba(197,165,90,0.02)] shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
              <CardContent className="p-4">
                <Button
                  onClick={() => setTryOnPanelOpen(true)}
                  className="w-full h-12 rounded-xl gap-2 text-base font-semibold"
                  style={{ backgroundColor: 'var(--brand-gold)', color: 'white' }}
                  data-testid="try-on-fabric-btn"
                >
                  <User className="w-5 h-5" /> Try On This Fabric
                </Button>
                <p className="text-xs text-center mt-2" style={{ color: 'var(--ink-muted)' }}>
                  See how this fabric looks on a client
                </p>
              </CardContent>
            </Card>
          )}

          {/* Availability */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Status</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Available for Orders</Label>
                <Switch checked={form.is_available} onCheckedChange={(v) => updateField('is_available', v)} disabled={isReadOnly} />
              </div>
            </CardContent>
          </Card>

          {/* Vendor Aliases (edit mode only) */}
          {!isNew && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Vendor Aliases</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {aliases.length > 0 ? (
                  <div className="space-y-2">
                    {aliases.map(alias => (
                      <div key={alias.id} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{alias.alias_text}</p>
                          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{alias.vendor_name}</p>
                        </div>
                        {isAdmin && (
                          <button onClick={() => handleDeleteAlias(alias.id)} className="p-1 rounded hover:bg-red-50" style={{ transition: 'background-color 150ms' }}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: 'var(--danger)' }} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No aliases yet</p>
                )}
                {isAdmin && (
                  <div className="space-y-2 pt-2">
                    <Separator />
                    <Input placeholder="Alias text" value={newAlias.alias_text} onChange={(e) => setNewAlias(prev => ({ ...prev, alias_text: e.target.value }))} className="h-10 rounded-lg text-sm" />
                    <Input placeholder="Vendor name" value={newAlias.vendor_name} onChange={(e) => setNewAlias(prev => ({ ...prev, vendor_name: e.target.value }))} className="h-10 rounded-lg text-sm" />
                    <Button variant="outline" size="sm" onClick={handleAddAlias} className="w-full h-9 rounded-lg text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Alias
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Swatch Image Lightbox */}
      {swatchLightboxOpen && form.swatch_image_url && (
        <Dialog open={swatchLightboxOpen} onOpenChange={setSwatchLightboxOpen}>
          <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none overflow-hidden">
            <button
              onClick={() => setSwatchLightboxOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
              <button
                onClick={() => setSwatchZoom(z => Math.max(z - 0.25, 0.5))}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                disabled={swatchZoom <= 0.5}
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </button>
              <span className="text-white text-sm min-w-[50px] text-center">{Math.round(swatchZoom * 100)}%</span>
              <button
                onClick={() => setSwatchZoom(z => Math.min(z + 0.25, 3))}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                disabled={swatchZoom >= 3}
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </button>
            </div>
            
            <div className="flex items-center justify-center p-8 min-h-[60vh]">
              <img
                src={resolveSwatchUrl(form.swatch_image_url)}
                alt="Swatch"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                style={{ transform: `scale(${swatchZoom})`, transition: 'transform 0.2s' }}
              />
            </div>
            
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <p className="text-white font-medium">{form.internal_code}</p>
              <p className="text-white/60 text-sm">{form.mill_name} · {form.composition}</p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Try On Fabric Panel */}
      <TryOnFabricPanel
        fabric={{
          id: fabricId,
          internal_code: form.internal_code,
          mill_name: form.mill_name,
          composition: form.composition,
          colour_primary: form.colour_primary,
          pattern_type: form.pattern_type,
          swatch_image_url: form.swatch_image_url,
        }}
        isOpen={tryOnPanelOpen}
        onClose={() => setTryOnPanelOpen(false)}
        onSuccess={() => {
          setTryOnPanelOpen(false);
          toast.success('Try-on saved!');
        }}
      />
    </div>
  );
}
